// Main App Component
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams, Outlet } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext.js';
import { useMultiChat } from './hooks/useMultiChat.js';
import { AgentChatProvider } from './contexts/AgentChatContext.js';
import { useAppTheme } from './contexts/ThemeContext';
import Layout from './components/layout/Layout.js';
import IconMenu from './components/layout/IconMenu.js';
import Sidebar from './components/layout/Sidebar.js';
import AgentChatManager from './components/agents/AgentChatManager.js';

const App = () => {
  const { isAuthenticated, logout, user, authToken } = useAuth();
  const { syncWithUser } = useAppTheme();

  // Sync theme preference from user profile
  useEffect(() => {
    if (user && authToken) {
      syncWithUser(user, authToken);
    }
  }, [user, authToken, syncWithUser]);
  const {
    chatSessions,
    activeSessionId,
    isLoading,
    error,
    success,
    startNewSession,
    switchSession,
    sendMessage,
    sendMessageStream,
    endSession,
    clearSessionMessages,
    getActiveSession,
    getAllSessions,
    clearNotifications,
    clearAllData,
    exportSessions,
    importSessions,
  } = useMultiChat(authToken);

  // Router hooks
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const [activeSection, setActiveSection] = useState('chat');

  // Initialize chat session when authenticated
  useEffect(() => {
    // No-op for now; keep hook for future initialization logic
  }, [isAuthenticated, chatSessions.size, startNewSession]);

  // Handle route parameter changes for session switching
  useEffect(() => {
    // Only handle chat session switching if we're on a chat route
    // A2A sessions routes (/agents/a2a-sessions/:sessionId) should not trigger chat session switching
    const isChatRoute = location.pathname.startsWith('/chat') || location.pathname === '/';
    const isA2ASessionRoute = location.pathname.startsWith('/agents/a2a-sessions');

    // Handle session switching when sessionId parameter changes (only for chat routes)
    if (isChatRoute && params.sessionId && params.sessionId !== activeSessionId) {
      const result = switchSession(params.sessionId);

      // If session doesn't exist in localStorage, redirect to chat page
      if (!result.success) {
        navigate('/chat/', { replace: true });
        return;
      }

      setActiveSection(params.sessionId);
    } else if (isA2ASessionRoute) {
      // A2A session route - don't try to switch chat sessions
    }

    // Set active section based on current route (for icon menu)
    if (location.pathname.startsWith('/ads-manager')) {
      setActiveSection('ads-manager');
    } else if (
      location.pathname.startsWith('/briefs') ||
      location.pathname.startsWith('/media-buys') ||
      location.pathname.startsWith('/creatives') ||
      location.pathname.startsWith('/products') ||
      location.pathname.startsWith('/packages') ||
      location.pathname.startsWith('/sellers') ||
      location.pathname.startsWith('/a2a-sessions')
    ) {
      setActiveSection('ad-manager');
    } else if (location.pathname.startsWith('/settings')) {
      setActiveSection('settings');
    } else if (location.pathname.startsWith('/analytics')) {
      setActiveSection('analytics');
    } else if (location.pathname.startsWith('/help')) {
      setActiveSection('help');
    } else if (location.pathname.startsWith('/agents/') && params.agentId) {
      setActiveSection(params.agentId);
    } else if (location.pathname.startsWith('/agents')) {
      setActiveSection('agent-studio');
    } else if (location.pathname.startsWith('/administration')) {
      setActiveSection('administration');
    } else if (location.pathname.startsWith('/development')) {
      setActiveSection('development');
    } else if (location.pathname.includes('/chat') || location.pathname === '/') {
      setActiveSection(params.sessionId || 'chat');
    }
  }, [location.pathname, params, activeSessionId, switchSession, navigate]);

  // Handle section changes from icon menu
  const handleIconSectionChange = (sectionId) => {
    if (sectionId === 'settings') {
      navigate('/settings/my-account');
    } else if (sectionId === 'files') {
      navigate('/files');
    } else if (sectionId === 'analytics') {
      navigate('/analytics');
    } else if (sectionId === 'help') {
      navigate('/help');
    } else if (sectionId === 'agent-studio') {
      navigate('/agents/studio');
    } else if (sectionId === 'administration') {
      navigate('/administration/accounts');
    } else if (sectionId === 'development') {
      navigate('/development/components');
    } else if (sectionId === 'ads-manager') {
      navigate('/ads-manager');
    } else if (sectionId === 'ad-manager') {
      navigate('/briefs');
    } else {
      setActiveSection(sectionId);
      navigate('/briefs');
    }
  };

  // Handle sidebar item changes
  const handleSidebarItemChange = async (itemId) => {
    if (itemId === 'clear-all-sessions') {
      handleClearAllSessions();
      return;
    }

    // Handle navigation to different sections
    if (itemId === 'agent-config') {
      navigate('/administration/agent-config');
    } else if (itemId === 'agent-tools') {
      navigate('/agent-tools');
    } else if (itemId === 'agent-settings') {
      navigate('/agent-settings');
    } else if (itemId.startsWith('session-')) {
      // Handle chat session switching
      handleSessionSwitch(itemId);
    } else {
      // This is not a session - it's likely a route item that should be handled by Sidebar
      // Don't try to switch it as a session
    }
  };

  // Handle session switching
  const handleSessionSwitch = (sessionId) => {
    const result = switchSession(sessionId);

    if (result.success) {
      setActiveSection(sessionId);
      navigate(`/chat/${sessionId}`);
    } else {
      console.error('Failed to switch session:', result.error);
    }
  };

  // Handle new session creation
  const handleNewSession = () => {
    const sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const sessionName = 'Chat';
    startNewSession(sessionId, sessionName);
    setActiveSection(sessionId);
    navigate(`/chat/${sessionId}`);
  };

  // Handle message sending
  const handleSendMessage = async (message, attachments = []) => {
    if (!activeSessionId) {
      console.error('No active session to send message to');
      return;
    }

    try {
      const result = await sendMessage(message, attachments);
      if (!result.success) {
        console.error('Failed to send message:', result.error);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  // Get current messages for the active session
  const getCurrentMessages = () => {
    const activeSession = getActiveSession();
    return activeSession ? activeSession.messages : [];
  };

  const handleAccountClick = () => {
    navigate('/account');
  };

  const handleSettingsClick = () => {
    navigate('/settings');
  };

  const handleLogout = () => {
    logout();
    setActiveSection('chat');
    navigate('/briefs');
  };

  // Handle clear all sessions
  const handleClearAllSessions = () => {
    clearAllData();
    setActiveSection('chat');
    // Always redirect to main chat page after clearing sessions
    navigate('/chat', { replace: true });
  };

  // Get current session data for child components
  const getCurrentSessionData = () => ({
    messages: getCurrentMessages(),
    onSendMessage: handleSendMessage,
    isLoading,
    sessionName: getActiveSession()?.name || 'Chat',
    onEndSession: () => endSession(activeSessionId),
    onClearMessages: () => clearSessionMessages(activeSessionId),
    authToken,
    agentId: params.agentId || activeSection,
    onNewSession: handleNewSession,
    activeSession: getActiveSession(),
  });

  // Main layout with dynamic content
  return (
    <AgentChatProvider authToken={authToken}>
      <Layout
        user={user}
        iconMenu={
          <IconMenu
            activeSection={activeSection}
            onSectionChange={handleIconSectionChange}
            user={user}
            onAccountClick={handleAccountClick}
            onSettingsClick={handleSettingsClick}
            onLogout={handleLogout}
          />
        }
        sidebar={
          <Sidebar
            activeSection={activeSection}
            onSectionChange={handleSidebarItemChange}
            authToken={authToken}
            user={user}
          />
        }
        mainContent={<Outlet context={getCurrentSessionData()} />}
      />
      {/* Floating Agent Chat Windows */}
      <AgentChatManager />
    </AgentChatProvider>
  );
};

export default App;
